import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Users2, Euro, Clock4, Target } from 'lucide-react';
import { StatsGridSkeleton } from '../LoadingSkeleton';
import { ProgressBar } from '../ProgressIndicator';
import { useIndustryWording } from '../../hooks/useIndustryWording';

interface StatCardProps {
  title: string;
  value: string;
  change: string;
  changeType: 'positive' | 'negative' | 'neutral';
  icon: React.ElementType;
  progressValue?: number;
  progressMax?: number;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, change, changeType, icon: Icon, progressValue, progressMax }) => {
  const changeColor = {
    positive: 'text-success',
    negative: 'text-danger',
    neutral: 'text-txt-secondary'
  }[changeType];

  return (
    <div className="bg-bg-card/90 backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-card border-0 p-4 sm:p-6 hover:shadow-card hover:scale-[1.02] transition-all duration-200 group">
      <div className="flex items-center justify-between mb-3">
        <div className="flex-1">
          <p className="text-xs sm:text-sm font-semibold text-txt-tertiary uppercase tracking-wider">{title}</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-txt mt-2 lg:mt-3">{value}</p>
        </div>
        <div className="p-2 sm:p-3 bg-bg-surface rounded-xl lg:rounded-2xl group-hover:bg-bg-surface-hover transition-all duration-300 shadow-card">
          <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-txt-secondary group-hover:scale-110 transition-transform duration-300" />
        </div>
      </div>
      
      {/* Progress Bar for percentage values */}
      {progressValue !== undefined && progressMax !== undefined && (
        <div className="mb-2">
          <ProgressBar 
            value={progressValue} 
            max={progressMax}
            color={progressValue >= 70 ? 'green' : progressValue >= 40 ? 'yellow' : 'red'}
            size="sm"
            showLabel={false}
          />
        </div>
      )}
      
      <p className={`text-xs sm:text-sm font-medium ${changeColor}`}>
        {change}
      </p>
    </div>
  );
};

interface DashboardStatsProps {
  leads?: any[];
  tasks?: any[];
  opportunities?: any[];
}

const toDate = (value: any): Date => {
  if (!value) return new Date(0);
  if (value instanceof Date) return value as Date;
  // Firestore Timestamp compatibility
  if (typeof value === 'object' && typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
};

const formatCurrency = (amount: number, locale: string = 'de-DE') => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(amount || 0));
};

const avg = (nums: number[]) => (nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0);

const hoursToHuman = (hours: number) => {
  if (!isFinite(hours)) return '0h';
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return m === 0 ? `${h} hrs` : `${h} hrs ${m} min`;
};

const DashboardStats: React.FC<DashboardStatsProps> = ({ leads = [], tasks = [], opportunities = [] }) => {
  const { t, i18n } = useTranslation('dashboard');
  const { getDashboardLabel } = useIndustryWording();
  
  const pctChange = (current: number, prev: number) => {
    if (prev === 0 && current === 0) return { text: t('stats.noChange'), type: 'neutral' as const };
    // No prior baseline to compute a percentage against — show a "new" badge
    // instead of a meaningless "+∞%".
    if (prev === 0) return { text: t('stats.new'), type: 'positive' as const };
    const diff = ((current - prev) / prev) * 100;
    const rounded = Math.abs(diff) >= 1 ? diff.toFixed(1) : diff.toFixed(2);
    return {
      text: `${diff >= 0 ? '+' : ''}${rounded}% ${t('stats.vsPrev')}`,
      type: diff > 0 ? ('positive' as const) : diff < 0 ? ('negative' as const) : ('neutral' as const)
    };
  };
  
  // Show skeleton while data is loading
  if (!leads && !tasks && !opportunities) {
    return <StatsGridSkeleton />;
  }

  const now = new Date();
  const msInDay = 24 * 60 * 60 * 1000;
  const startThis = new Date(now.getTime() - 30 * msInDay);
  const startPrev = new Date(now.getTime() - 60 * msInDay);

  // Total Leads
  const totalLeads = leads.length;
  const leadsThis = leads.filter(l => toDate(l.createdAt) >= startThis).length;
  const leadsPrev = leads.filter(l => toDate(l.createdAt) >= startPrev && toDate(l.createdAt) < startThis).length;
  const leadsChange = pctChange(leadsThis, leadsPrev);

  // Conversion Rate (opportunities won / total opportunities)
  const totalOpps = opportunities.length;
  const wonOpps = opportunities.filter(o => o.stage === 'won').length;
  const conversionRate = totalOpps === 0 ? 0 : (wonOpps / totalOpps) * 100;
  const oppsThis = opportunities.filter(o => toDate(o.updatedAt || o.createdAt) >= startThis);
  const oppsPrev = opportunities.filter(o => toDate(o.updatedAt || o.createdAt) >= startPrev && toDate(o.updatedAt || o.createdAt) < startThis);
  const wonThis = oppsThis.filter(o => o.stage === 'won').length;
  const wonPrev = oppsPrev.filter(o => o.stage === 'won').length;
  const convThis = oppsThis.length ? (wonThis / oppsThis.length) * 100 : 0;
  const convPrev = oppsPrev.length ? (wonPrev / oppsPrev.length) * 100 : 0;
  const conversionChange = pctChange(convThis, convPrev);

  // Pipeline Value (open deals only)
  const isOpen = (s: string) => !['won', 'lost'].includes(s);
  const openOpps = opportunities.filter(o => isOpen(o.stage));
  const pipelineValue = openOpps.reduce((sum, o) => sum + (typeof o.value === 'number' ? o.value : Number(o.value) || 0), 0);
  const openThis = oppsThis.filter(o => isOpen(o.stage));
  const openPrev = oppsPrev.filter(o => isOpen(o.stage));
  const pipeThis = openThis.reduce((sum, o) => sum + (typeof o.value === 'number' ? o.value : Number(o.value) || 0), 0);
  const pipePrev = openPrev.reduce((sum, o) => sum + (typeof o.value === 'number' ? o.value : Number(o.value) || 0), 0);
  const pipelineChange = pctChange(pipeThis, pipePrev);

  // Response Time (avg time from lead createdAt to first activity/email sent)
  const firstResponseHoursForLead = (lead: any): number | null => {
    const created = toDate(lead.createdAt);
    const emailSentTimes = Array.isArray(lead.emails)
      ? lead.emails
          .filter((e: any) => (e.direction === 'sent' || e.direction === 'received') && e.timestamp)
          .map((e: any) => toDate(e.timestamp).getTime())
      : [];
    const activityTimes = Array.isArray(lead.activities)
      ? lead.activities
          .filter((a: any) => a && a.createdAt)
          .map((a: any) => toDate(a.createdAt).getTime())
      : [];
    const allTimes = [...emailSentTimes, ...activityTimes].filter(Boolean);
    if (allTimes.length === 0) return null;
    const first = Math.min(...allTimes);
    const diffHrs = (first - created.getTime()) / (60 * 60 * 1000);
    return diffHrs >= 0 ? diffHrs : null;
  };

  const responseHoursAll = leads
    .map(firstResponseHoursForLead)
    .filter((v): v is number => typeof v === 'number');
  const avgResponseHours = avg(responseHoursAll);

  const responseHoursThis = leads
    .filter(l => toDate(l.createdAt) >= startThis)
    .map(firstResponseHoursForLead)
    .filter((v): v is number => typeof v === 'number');
  const responseHoursPrev = leads
    .filter(l => toDate(l.createdAt) >= startPrev && toDate(l.createdAt) < startThis)
    .map(firstResponseHoursForLead)
    .filter((v): v is number => typeof v === 'number');
  const respThis = avg(responseHoursThis);
  const respPrev = avg(responseHoursPrev);
  const responseChange = pctChange(respPrev === 0 ? 0 : respPrev, respPrev === 0 ? 0 : respThis); // lower is better → invert comparison text
  // For user-facing clarity, we override text to indicate direction in time terms
  const responseChangeText = (() => {
    if (respPrev === 0 && respThis === 0) return t('stats.noChange');
    if (respPrev === 0 && respThis > 0) return `+∞% ${t('stats.vsPrev')}`;
    const diff = ((respPrev - respThis) / (respPrev || 1)) * 100; // improvement if positive
    const rounded = Math.abs(diff) >= 1 ? diff.toFixed(1) : diff.toFixed(2);
    return `${diff >= 0 ? '+' : ''}${rounded}% faster ${t('stats.vsPrev')}`;
  })();
  const responseChangeType = (() => {
    if (respPrev === 0 && respThis === 0) return 'neutral' as const;
    return respThis <= respPrev ? ('positive' as const) : ('negative' as const);
  })();

  // Revenue (from won opportunities in last 30 days)
  // NOTE: This shows won opportunity value for context only
  // Actual revenue tracking happens via customer payment schedules to prevent double-counting
  const safeValue = (o: any) => typeof o.value === 'number' ? o.value : Number(o.value) || 0;
  const revenueThis = oppsThis.filter(o => o.stage === 'won').reduce((sum, o) => sum + safeValue(o), 0);
  const revenuePrev = oppsPrev.filter(o => o.stage === 'won').reduce((sum, o) => sum + safeValue(o), 0);
  const revenueChange = pctChange(revenueThis, revenuePrev);

  // Total Opportunities
  const totalOpportunities = opportunities.length;
  const oppsCountThis = oppsThis.length;
  const oppsCountPrev = oppsPrev.length;
  const oppsCountChange = pctChange(oppsCountThis, oppsCountPrev);

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';

  const stats = [
    {
      title: t('stats.activeLeads'),
      value: `${totalLeads.toLocaleString(locale)}`,
      change: `${leadsChange.text}`,
      changeType: leadsChange.type,
      icon: Users2
    },
    {
      title: t('stats.revenueThisMonth'),
      value: `${formatCurrency(revenueThis, locale)}`,
      change: `${revenueChange.text}`,
      changeType: revenueChange.type,
      icon: Euro
    },
    {
      title: t('stats.pipelineValue'),
      value: `${formatCurrency(pipelineValue, locale)}`,
      change: `${pipelineChange.text}`,
      changeType: pipelineChange.type,
      icon: TrendingUp
    },
    {
      title: 'Opportunities',
      value: `${totalOpportunities.toLocaleString(locale)}`,
      change: `${oppsCountChange.text}`,
      changeType: oppsCountChange.type,
      icon: Target
    }
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-4 lg:gap-6 mb-6">
      {stats.map((stat, index) => (
        <StatCard key={index} {...stat} />
      ))}
    </div>
  );
};

export default DashboardStats;
